'use client'

import { useRef } from 'react'

import { cn } from '@/core/utils/common'

import { gsap, useGSAP } from '@/features/veluvit/lib/gsap'

/** 로고 둘레를 도는 별 — 궤도 반지름·색·크기·시작 각도 */
const ORBITS = [
  { r: 62, color: 'text-lilac', size: 'size-4', start: 20 },
  { r: 78, color: 'text-gem', size: 'size-3', start: 160 },
  { r: 92, color: 'text-white', size: 'size-2.25', start: 280 },
] as const

/**
 * 로딩 표시 — 로고 "veluvit." 둘레를 작은 별 셋이 서로 다른 속도로 돌며 반짝이고, 코랄 마침표가 숨 쉬듯 빛난다.
 * 글자 안내는 화면 읽기용(label)으로만 둔다.
 * core Loader 는 전역 스토어로 여닫는 "잠시만 기다려 주세요" 모달이라, 첫 진입 전체 화면의 브랜드 모션으로 쓸 수 없어 따로 둔다.
 */
export default function LogoLoader({
  label,
  className,
}: {
  label: string
  className?: string
}) {
  const scope = useRef<HTMLDivElement>(null)
  useGSAP(
    () => {
      gsap.utils
        .toArray<HTMLElement>('[data-orbit]', scope.current)
        .forEach((orbit, i) => {
          gsap.to(orbit, {
            rotate: i % 2 ? -360 : 360,
            duration: 5 + i * 1.6,
            ease: 'none',
            repeat: -1,
          })
        })
      gsap.utils
        .toArray<HTMLElement>('[data-star]', scope.current)
        .forEach((star, i) => {
          gsap.fromTo(
            star,
            { scale: 0.4, opacity: 0.3 },
            {
              scale: 1,
              opacity: 1,
              duration: 0.7 + i * 0.15,
              yoyo: true,
              repeat: -1,
              ease: 'sine.inOut',
            }
          )
        })
      gsap.to('[data-period]', {
        scale: 1.35,
        boxShadow: '0 0 18px 4px #ff6a55',
        duration: 0.9,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      })
    },
    { scope }
  )
  return (
    <div
      ref={scope}
      className={cn('relative grid size-56 place-items-center', className)}
    >
      {ORBITS.map((o) => (
        // 궤도 판을 돌리면 그 위 별이 원을 그린다. 시작 각도는 CSS rotate, 회전은 GSAP transform 이라 겹치지 않는다
        <span
          key={o.r}
          aria-hidden="true"
          data-orbit
          className="absolute inset-0"
          style={{ rotate: `${o.start}deg` }}
        >
          <span
            data-star
            className={cn('absolute left-1/2 -translate-x-1/2', o.color)}
            style={{ top: `calc(50% - ${o.r}px)` }}
          >
            <svg viewBox="0 0 24 24" className={cn('block', o.size)}>
              <path
                d="M12 0c.8 6.2 5.8 11.2 12 12-6.2.8-11.2 5.8-12 12-.8-6.2-5.8-11.2-12-12C6.2 11.2 11.2 6.2 12 0z"
                fill="currentColor"
              />
            </svg>
          </span>
        </span>
      ))}
      <span aria-hidden="true" className="relative flex items-end gap-1">
        <span className="text-c40 font-black tracking-[-0.045em] text-white">
          veluvit
        </span>
        <i data-period className="mb-2.5 size-2.5 rounded-full bg-coral" />
      </span>
      <span className="sr-only">{label}</span>
    </div>
  )
}
